import {Database, useRpcQuery} from '@tnc/shared';
import {Map} from '@vis.gl/react-google-maps';

import {profileDetailsDataItemT, profileDetailsTabListTabT} from '@/lib/types/form.types';

import {dateFormatter, usdFormatter} from '@/lib/helpers';

import MarketDetailDistributors from '@/pages/(shared)/market/components/marketDetailDistributors';
import MarketDetailRetailers from '@/pages/(shared)/market/components/marketDetailRetailers';
import {useMarket} from '@/pages/(shared)/market/providers/marketDetailsProvider';

import {ProfileCardComponent, ProfileDetailsTabListComponent, profileCardSectionT} from '@/components';
import Page from '@/components/Page';

export function MarketDetails() {
  const {market} = useMarket();

  /**
   * Get order data
   */
  const {data: orderData} = useRpcQuery({
    functionName: 'market_order_summary',
    functionArgs: {market_id_input: market?.market_id ?? 0},
    sortOption: {column: 'order_date', direction: 'desc', label: 'Order Date'},
    enabled: !!market,
  });

  const headerInfo = {
    // Use the market name
    header: market?.market_name || '',

    // Use the creation date as a subheader
    subHeader: market?.date_created ? `Established on ${new Date(market.date_created).toLocaleDateString()}` : '',

    // Include dynamic content in perks, like city and state, and any other relevant perks
    perks: [],
  };

  const sections: profileCardSectionT[] = [
    {
      title: 'Market Information',
      items: [{header: 'Name', content: market?.market_name}],
    },
  ];

  const tabs: profileDetailsTabListTabT[] = [
    {
      id: 'distributors',
      title: 'Distributors',
      icon: 'ri-store-2-line',
      content: <MarketDetailDistributors />,
    },
    {
      id: 'retailers',
      title: 'Retailers',
      icon: 'ri-store-2-line',
      content: <MarketDetailRetailers />,
    },
    {
      id: 'inventory',
      title: 'Inventory',
      icon: 'ri-shopping-cart-2-line',
      content: <div>TODO: Inventory</div>,
    },
    {
      id: 'positions',
      title: 'Positions',
      icon: 'ri-user-2-line',
      content: <div>TODO: Positions</div>,
    },
  ];

  const processedDataItems: profileDetailsDataItemT[] = [
    {
      type: 'generic',
      title: 'Pending Orders',
      data:
        orderData?.data
          .filter(
            (order: Database['public']['Functions']['distributor_order_summary']['Returns'][0]) =>
              order.order_group === 'pending'
          )
          .map((order: Database['public']['Functions']['distributor_order_summary']['Returns'][0]) => ({
            item: `Order #${order.order_id}`,
            texts: [
              {text1: 'Order Date:', text2: dateFormatter(order.order_date, 'short', true)},
              {text1: 'Type:', text2: order.order_type},
              {text1: 'Order Status:', text2: order.order_status},
              {text1: 'Order Balance:', text2: usdFormatter.format(order.order_balance)},
            ],
          })) || [],
    },
    {
      type: 'generic',
      title: 'In Progress Orders',
      data:
        orderData?.data
          .filter(
            (order: Database['public']['Functions']['distributor_order_summary']['Returns'][0]) =>
              order.order_group === 'inprogress'
          )
          .map((order: Database['public']['Functions']['distributor_order_summary']['Returns'][0]) => ({
            item: `Order #${order.order_id}`,
            texts: [
              {text1: 'Order Date:', text2: dateFormatter(order.order_date, 'short', true)},
              {text1: 'Type:', text2: order.order_type},
              {text1: 'Order Status:', text2: order.order_status},
              {text1: 'Order Balance:', text2: usdFormatter.format(order.order_balance)},
            ],
          })) || [],
    },
    {
      type: 'generic',
      title: 'Accepted/Rejected Orders',
      data:
        orderData?.data
          .filter((order: Database['public']['Functions']['distributor_order_summary']['Returns'][0]) =>
            ['accepted', 'rejected'].includes(order.order_group)
          )
          .map((order: Database['public']['Functions']['distributor_order_summary']['Returns'][0]) => ({
            item: `Order #${order.order_id}`,
            texts: [
              {text1: 'Order Date:', text2: dateFormatter(order.order_date, 'short', true)},
              {text1: 'Type:', text2: order.order_type},
              {text1: 'Order Status:', text2: order.order_status},
              {text1: 'Order Balance:', text2: usdFormatter.format(order.order_balance)},
            ],
          })) || [],
    },
  ];

  return (
    <Page currentPage={market?.market_name} activePage="Markets">
      <div className="grid grid-cols-12 gap-x-6">
        <ProfileCardComponent
          avatar={'https://placekeanu.com/100/100'}
          name={market?.market_name || ''}
          headerInfo={headerInfo}
          sections={sections}
        />
        <ProfileDetailsTabListComponent
          tabs={tabs}
          dataItems={processedDataItems}
          className="col-span-12 xl:col-span-12 xxl:col-span-8"
        >
          <div className="mb-6 rounded-lg bg-slate-500 p-4 shadow-md">
            <Map
              /* TODO: Need Geo-coded addresses */
              style={{width: '100%', height: '200px'}}
              defaultCenter={{lat: 34.0284727, lng: -118.2911198}}
              defaultZoom={15}
              gestureHandling={'greedy'}
              disableDefaultUI={true}
            />
          </div>
        </ProfileDetailsTabListComponent>
      </div>
    </Page>
  );
}
